import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Fab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Search,
  Add,
  Visibility,
  Edit,
  FilterList,
  CalendarToday,
  Store,
  Inventory,
  Delete,
  Download
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAppData } from '../../context/AppDataContext';
import { getOrderPurchases, deleteOrder } from '../../services/api';
import { FEATURE_PERMISSIONS } from '../../utils/frontendPermissions';

const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
    case 'done':
      return { color: 'success', label: 'Completed' };
    case 'pending':
      return { color: 'warning', label: 'Pending' };
    case 'in_progress':
      return { color: 'info', label: 'In Progress' };
    default:
      return { color: 'default', label: 'Unknown' };
  }
};

const InventoryImportList = () => {
  const navigate = useNavigate();
  const { getStoreList, hasFrontendPermission } = useAppData();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Filter states
  const [selectedClient, setSelectedClient] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleteConfirmationCode, setDeleteConfirmationCode] = useState('');
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Check permissions
  const canCreate = hasFrontendPermission(FEATURE_PERMISSIONS.INVENTORY_IMPORT.CREATE);

  // Load orders from API
  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        status: activeTab === 0 ? 'pending' : 'confirmed',
        ...(dateFrom && { dateFrom: dateFrom.toISOString().split('T')[0] }),
        ...(dateTo && { dateTo: dateTo.toISOString().split('T')[0] })
      };
      
      const response = await getOrderPurchases(params);
      setOrders(response.data || []);
    } catch (err) {
      setError('Failed to load orders. Please try again.');
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [activeTab, dateFrom, dateTo]);

  useEffect(() => {
    // Filter orders based on search term
    const filtered = orders.filter(order =>
      order.unique_key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.products?.some(product => 
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.SKU?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
    setFilteredOrders(filtered);
  }, [searchTerm, orders]);

  const handleViewDetail = (order) => {
    navigate(`/inventory-import/${order._id}`);
  };

  const handleCreateNew = () => {
    if (!selectedClient) {
      return; // Don't navigate if no client is selected
    }
    navigate('/inventory-import/create', { 
      state: { clientId: selectedClient } 
    });
  };

  // Generate random 8-digit confirmation code
  const generateConfirmationCode = () => {
    return Math.floor(10000000 + Math.random() * 90000000).toString();
  };

  const handleDeleteClick = (order, e) => {
    e.stopPropagation();
    setOrderToDelete(order);
    const confirmationCode = generateConfirmationCode();
    setDeleteConfirmationCode(confirmationCode);
    setDeleteConfirmationInput('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setOrderToDelete(null);
    setDeleteConfirmationCode('');
    setDeleteConfirmationInput('');
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;
    
    // Validate that the entered confirmation code matches
    if (deleteConfirmationInput !== deleteConfirmationCode) {
      setError('Confirmation code does not match. Please enter the correct code.');
      return;
    }

    setIsDeleting(true);
    setError(null);
    
    try {
      await deleteOrder(orderToDelete._id);
      // Reload orders after successful deletion
      await loadOrders();
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      setDeleteConfirmationCode('');
      setDeleteConfirmationInput('');
    } catch (err) {
      setError('Failed to delete order. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getTotalItems = (products) => {
    return products?.reduce((total, product) => total + (product.qty || 0), 0) || 0;
  };

  const getTotalSerials = (products) => {
    return products?.reduce((total, product) => {
      return total + (product.serials?.length || 0);
    }, 0) || 0;
  };

  const getTotalDefects = (products) => {
    return products?.reduce((total, product) => {
      const defects = product.defects || product.Defects || [];
      return total + (defects.length || 0);
    }, 0) || 0;
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/import-template.csv';
    link.download = 'import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ px: { xs: 0, sm: 1 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 3, sm: 6 } }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            color: '#1e293b', 
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }}>
            Inventory Import Management
          </Typography>
          <Typography variant="body1" sx={{ 
            color: '#64748b',
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}>
            Manage and track all warehouse inventory import orders
          </Typography>
        </Box>

        {/* Client Selection */}
        <Card sx={{ mb: { xs: 2, sm: 4 } }}>
          <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
            <Grid container spacing={{ xs: 1, sm: 2 }} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Select Client</InputLabel>
                  <Select
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    startAdornment={<Store />}
                  >
                    {getStoreList().map((store) => (
                      <MenuItem key={store.id} value={store.id}>
                        {store.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={8}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={handleDownloadTemplate}
                    sx={{ minHeight: 32 }}
                  >
                    Download Import Template CSV
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Compact Filter Section */}
        <Card sx={{ mb: { xs: 3, sm: 6 } }}>
          <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
            <Grid container spacing={{ xs: 1, sm: 1.5 }} alignItems="center">
              {/* Status Tabs */}
              <Grid item xs={12} sm={6} md={2}>
                <Tabs 
                  value={activeTab} 
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ 
                    minHeight: 'auto',
                    '& .MuiTab-root': {
                      minHeight: 32,
                      fontSize: '0.75rem',
                      py: 0.5
                    }
                  }}
                >
                  <Tab label="Pending" />
                  <Tab label="Done" />
                </Tabs>
              </Grid>

              {/* Date Filters */}
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="From"
                  value={dateFrom}
                  onChange={setDateFrom}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="To"
                  value={dateTo}
                  onChange={setDateTo}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true
                    }
                  }}
                />
              </Grid>

              {/* Clear Dates Button */}
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setDateFrom(null);
                    setDateTo(null);
                  }}
                  startIcon={<CalendarToday />}
                  fullWidth
                  sx={{ minHeight: 32 }}
                >
                  Clear
                </Button>
              </Grid>

              {/* Search Field */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12} sm={6} md={3}>
                <Box className="flex gap-2">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterList />}
                    sx={{ 
                      flex: 1,
                      minHeight: 32,
                      px: 2
                    }}
                  >
                    Filter
                  </Button>
                  {canCreate && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={handleCreateNew}
                      disabled={!selectedClient}
                      className="bg-blue-600 hover:bg-blue-700"
                      sx={{ 
                        flex: 1,
                        minHeight: 32,
                        px: 2,
                        opacity: !selectedClient ? 0.5 : 1
                      }}
                    >
                      New
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Orders Table */}
        <Card>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 300, sm: 650 } }}>
              <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Items</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Products</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>Performed by</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order) => {
                    const statusInfo = getStatusColor(order.status);
                    const totalItems = getTotalItems(order.products);
                    const showConfirmedButton = activeTab === 1 && statusInfo.label === 'Unknown';
                    
                    return (
                      <TableRow
                        key={order._id}
                        sx={{ 
                          '&:hover': { backgroundColor: '#f8fafc' },
                          cursor: 'pointer'
                        }}
                        onClick={() => handleViewDetail(order)}
                      >
                        <TableCell sx={{ 
                          fontWeight: 500, 
                          color: '#2563eb',
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          maxWidth: { xs: '120px', sm: 'none' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {order.unique_key}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          display: { xs: 'none', sm: 'table-cell' }
                        }}>
                          {formatDate(order.createdAt)}
                        </TableCell>
                        <TableCell>
                          {showConfirmedButton ? (
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              sx={{
                                fontSize: { xs: '0.625rem', sm: '0.75rem' },
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 2
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Confirmed
                            </Button>
                          ) : (
                            <Chip
                              label={statusInfo.label}
                              color={statusInfo.color}
                              size="small"
                              sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          display: { xs: 'none', md: 'table-cell' }
                        }}>
                          {totalItems}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          display: { xs: 'none', md: 'table-cell' }
                        }}>
                          {order.products?.length || 0} products
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          display: { xs: 'none', lg: 'table-cell' }
                        }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {/* Creator Names - Yellow Tags */}
                            {(order.creatorNames && order.creatorNames.length > 0) ? (
                              order.creatorNames.map((name, index) => (
                                <Chip
                                  key={`creator-${index}`}
                                  label={name}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#fbbf24',
                                    color: '#78350f',
                                    fontSize: { xs: '0.625rem', sm: '0.75rem' },
                                    height: '24px',
                                    '& .MuiChip-label': {
                                      px: 1
                                    }
                                  }}
                                />
                              ))
                            ) : (
                              <Chip
                                label="unknown"
                                size="small"
                                sx={{
                                  backgroundColor: '#fbbf24',
                                  color: '#78350f',
                                  fontSize: { xs: '0.625rem', sm: '0.75rem' },
                                  height: '24px',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            )}
                            {/* Serial Inputter Names - Blue Tags */}
                            {(order.serialInputterNames && order.serialInputterNames.length > 0) ? (
                              order.serialInputterNames.map((name, index) => (
                                <Chip
                                  key={`serial-${index}`}
                                  label={name}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#60a5fa',
                                    color: '#1e3a8a',
                                    fontSize: { xs: '0.625rem', sm: '0.75rem' },
                                    height: '24px',
                                    '& .MuiChip-label': {
                                      px: 1
                                    }
                                  }}
                                />
                              ))
                            ) : (
                              <Chip
                                label="unknown"
                                size="small"
                                sx={{
                                  backgroundColor: '#60a5fa',
                                  color: '#1e3a8a',
                                  fontSize: { xs: '0.625rem', sm: '0.75rem' },
                                  height: '24px',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            )}
                            {/* Order Confirmer Names - Green Tags */}
                            {(order.orderConfirmerNames && order.orderConfirmerNames.length > 0) ? (
                              order.orderConfirmerNames.map((name, index) => (
                                <Chip
                                  key={`confirmer-${index}`}
                                  label={name}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#4ade80',
                                    color: '#14532d',
                                    fontSize: { xs: '0.625rem', sm: '0.75rem' },
                                    height: '24px',
                                    '& .MuiChip-label': {
                                      px: 1
                                    }
                                  }}
                                />
                              ))
                            ) : (
                              <Chip
                                label="unknown"
                                size="small"
                                sx={{
                                  backgroundColor: '#4ade80',
                                  color: '#14532d',
                                  fontSize: { xs: '0.625rem', sm: '0.75rem' },
                                  height: '24px',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetail(order);
                              }}
                              sx={{ color: '#2563eb' }}
                            >
                              <Visibility />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) => handleDeleteClick(order, e)}
                              sx={{ color: '#dc2626' }}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Floating Action Button */}
        {canCreate && (
          <Fab
            color="primary"
            aria-label="add"
            onClick={handleCreateNew}
            disabled={!selectedClient}
            sx={{
              position: 'fixed',
              bottom: { xs: 16, sm: 24 },
              right: { xs: 16, sm: 24 },
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              color: 'white',
              opacity: !selectedClient ? 0.5 : 1,
              width: { xs: 48, sm: 56 },
              height: { xs: 48, sm: 56 },
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)',
                color: 'white',
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
              }
            }}
          >
            <Add />
          </Fab>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Delete Order
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 3 }}>
              This action cannot be undone. Please enter the confirmation code to confirm deletion.
            </DialogContentText>
            {orderToDelete && (
              <>
                {/* Order Information */}
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontWeight: 600 }}>
                    Order Information:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2563eb', mb: 0.5 }}>
                          {orderToDelete.products?.length || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Products
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#16a34a', mb: 0.5 }}>
                          {getTotalSerials(orderToDelete.products)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Serials
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#dc2626', mb: 0.5 }}>
                          {getTotalDefects(orderToDelete.products)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Errors
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Confirmation Code */}
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    Confirmation code:
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700, 
                      color: '#2563eb',
                      fontFamily: 'monospace',
                      letterSpacing: 2
                    }}
                  >
                    {deleteConfirmationCode}
                  </Typography>
                </Box>
              </>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="Enter Confirmation Code"
              type="text"
              fullWidth
              variant="outlined"
              value={deleteConfirmationInput}
              onChange={(e) => setDeleteConfirmationInput(e.target.value)}
              placeholder="Enter the 8-digit code shown above"
              error={deleteConfirmationInput !== '' && deleteConfirmationInput !== deleteConfirmationCode}
              helperText={
                deleteConfirmationInput !== '' && deleteConfirmationInput !== deleteConfirmationCode
                  ? 'Confirmation code does not match'
                  : ''
              }
              inputProps={{
                maxLength: 8,
                pattern: '[0-9]*',
                inputMode: 'numeric'
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={
                isDeleting ||
                !deleteConfirmationInput ||
                deleteConfirmationInput !== deleteConfirmationCode
              }
              sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default InventoryImportList;